/*
  GrillDaddy 3D product viewer.
  Opens a fullscreen modal with a rotatable <model-viewer> scene and
  hover/tap hotspots that describe product features.

  Hotspot line format (one per line, pipe separated):
    position | normal | title | description
  e.g. "0 0.12 0.04 | 0 1 0 | Steam Reservoir | Fill with water for steam power"
*/
(function () {
  'use strict';

  var MODEL_VIEWER_SRC = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js';
  var loaderPromise = null;

  function loadModelViewer() {
    if (window.customElements && window.customElements.get('model-viewer')) {
      return Promise.resolve();
    }
    if (!loaderPromise) {
      loaderPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.type = 'module';
        script.src = MODEL_VIEWER_SRC;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return loaderPromise;
  }

  function parseHotspots(text) {
    if (!text) return [];
    return text
      .split('\n')
      .map(function (line) { return line.trim(); })
      .filter(Boolean)
      .map(function (line) {
        var parts = line.split('|').map(function (p) { return p.trim(); });
        if (parts.length < 3) return null;
        return {
          position: parts[0],
          normal: parts[1] || '0 1 0',
          title: parts[2] || '',
          text: parts[3] || ''
        };
      })
      .filter(Boolean);
  }

  var GD3DViewer = function () {
    return Reflect.construct(HTMLElement, [], GD3DViewer);
  };
  GD3DViewer.prototype = Object.create(HTMLElement.prototype);
  GD3DViewer.prototype.constructor = GD3DViewer;
  Object.setPrototypeOf(GD3DViewer, HTMLElement);

  GD3DViewer.prototype.connectedCallback = function () {
    var configEl = this.querySelector('[data-gd3d-config]');
    if (!configEl) return;
    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    this.modal = this.querySelector('[data-gd3d-modal]');
    this.stage = this.querySelector('[data-gd3d-stage]');
    this.isOpen = false;
    this.mv = null;

    var self = this;
    var openBtn = this.querySelector('[data-gd3d-open]');
    if (openBtn) {
      openBtn.addEventListener('click', function () { self.open(); });
    }
    var closeBtn = this.querySelector('[data-gd3d-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () { self.close(); });
    }
    this.modal.addEventListener('click', function (e) {
      if (e.target === self.modal) self.close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen) self.close();
    });
  };

  GD3DViewer.prototype.open = function () {
    var self = this;
    this.modal.hidden = false;
    this.isOpen = true;
    document.documentElement.classList.add('gd3d-lock');
    loadModelViewer()
      .then(function () { self.mount(); })
      .catch(function () {
        self.stage.innerHTML = '<p class="gd3d-error">The 3D viewer could not be loaded. Please try again.</p>';
      });
  };

  GD3DViewer.prototype.close = function () {
    this.modal.hidden = true;
    this.isOpen = false;
    document.documentElement.classList.remove('gd3d-lock');
    if (this.mv) this.mv.removeAttribute('auto-rotate');
  };

  GD3DViewer.prototype.mount = function () {
    if (this.mv) {
      if (this.config.autoRotate) this.mv.setAttribute('auto-rotate', '');
      return;
    }
    var c = this.config;
    var mv = document.createElement('model-viewer');
    mv.setAttribute('src', c.src);
    if (c.poster) mv.setAttribute('poster', c.poster);
    mv.setAttribute('alt', c.alt || '3D product model');
    mv.setAttribute('camera-controls', '');
    mv.setAttribute('touch-action', 'pan-y');
    mv.setAttribute('shadow-intensity', '1');
    mv.setAttribute('exposure', '1');
    mv.setAttribute('interaction-prompt', 'auto');
    if (c.autoRotate) {
      mv.setAttribute('auto-rotate', '');
      mv.setAttribute('auto-rotate-delay', '1500');
    }
    mv.className = 'gd3d-model';

    var hotspots = parseHotspots(c.hotspots);
    hotspots.forEach(function (h, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gd3d-hotspot';
      btn.setAttribute('slot', 'hotspot-' + (i + 1));
      btn.setAttribute('data-position', h.position);
      btn.setAttribute('data-normal', h.normal);
      btn.setAttribute('aria-label', h.title);
      btn.innerHTML =
        '<span class="gd3d-hotspot__dot" aria-hidden="true"><span class="gd3d-hotspot__num">' + (i + 1) + '</span></span>' +
        '<span class="gd3d-hotspot__card"><strong>' + h.title + '</strong>' + (h.text ? '<span>' + h.text + '</span>' : '') + '</span>';
      var deactivateAll = function () {
        mv.querySelectorAll('.gd3d-hotspot.is-active').forEach(function (el) {
          el.classList.remove('is-active');
        });
      };
      // Hover shows the card on desktop; tap toggles it on touch devices.
      btn.addEventListener('mouseenter', function () {
        deactivateAll();
        btn.classList.add('is-active');
      });
      btn.addEventListener('mouseleave', function () {
        btn.classList.remove('is-active');
      });
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var wasActive = btn.classList.contains('is-active');
        deactivateAll();
        if (!wasActive) btn.classList.add('is-active');
      });
      mv.appendChild(btn);
    });

    if (c.debug) {
      var readout = document.createElement('div');
      readout.className = 'gd3d-debug';
      readout.textContent = 'Setup mode: click the model to get hotspot coordinates';
      this.stage.appendChild(readout);
      mv.addEventListener('click', function (event) {
        var hit = mv.positionAndNormalFromPoint(event.clientX, event.clientY);
        if (!hit) return;
        var fmt = function (v) {
          return [v.x, v.y, v.z].map(function (n) { return n.toFixed(3); }).join(' ');
        };
        var line = fmt(hit.position) + ' | ' + fmt(hit.normal) + ' | Feature title | Feature description';
        readout.textContent = line;
        if (navigator.clipboard) navigator.clipboard.writeText(line).catch(function () {});
      });
    }

    this.stage.appendChild(mv);
    this.mv = mv;
  };

  if (!window.customElements.get('gd-3d-viewer')) {
    window.customElements.define('gd-3d-viewer', GD3DViewer);
  }
})();
